/**
 * `/channels` page body — adapter catalog + per-team binding CRUD.
 *
 * Data sources:
 *
 *   - `GET /v1/channels/adapters` — every `ChannelAdapter` registered in
 *     `kos_channels.registry`. Static within a process so we cache for
 *     the page lifetime.
 *   - `GET /v1/channels/bindings` — live channel ↔ agent bindings.
 *     Refetched after every mutation.
 *   - `GET /v1/agents` — populates the agent picker in the binding form.
 *
 * Mutations require `team_admin`; the browser surface mirrors the backend
 * gate by hiding the admin actions when the caller is read-only.
 */

import { Globe, Hexagon, Key, Loader2, MessageCircle, Pencil, Plus, Trash2 } from "lucide-react";
import type { ComponentType } from "react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/i18n-dict";
import type {
  AdapterDescriptor,
  AdapterListResponse,
  AdapterStatus,
  ChannelBindingListResponse,
  ChannelBindingSummary,
  CreateChannelBindingRequest,
  UpdateChannelBindingRequest,
} from "@/lib/types";

// ────────────────────────────────────────────────────────────────────
// Adapter visuals — purely decorative metadata that the backend doesn't
// know about. We map known kinds to icons; unknown kinds get a neutral
// default. Do NOT use this map for behaviour — drive everything off the
// descriptor coming from the API.
// ────────────────────────────────────────────────────────────────────

const ICON_BY_KIND: Record<string, ComponentType<{ className?: string }>> = {
  feishu: MessageCircle,
  web: Globe,
  api: Key,
};

function adapterIcon(kind: string): ComponentType<{ className?: string }> {
  return ICON_BY_KIND[kind] ?? Hexagon;
}

interface AgentOption {
  id: string;
  name: string;
}

interface ChannelsBrowserProps {
  /** When false, mutation actions are hidden — backend still gates by
   *  role, but it's nicer to not even show buttons that will 403. */
  isAdmin: boolean;
}

export function ChannelsBrowser({ isAdmin }: ChannelsBrowserProps) {
  const t = useT();
  const [adapters, setAdapters] = useState<AdapterDescriptor[] | null>(null);
  const [adaptersError, setAdaptersError] = useState<string | null>(null);

  const [bindings, setBindings] = useState<ChannelBindingSummary[] | null>(null);
  const [bindingsError, setBindingsError] = useState<string | null>(null);

  const [agents, setAgents] = useState<AgentOption[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  // Pre-pick an adapter from the catalog when the user clicks "Configure"
  // on a card.
  const [createKind, setCreateKind] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChannelBindingSummary | null>(null);

  useEffect(() => {
    void refresh();
    // Empty deps — page is mounted once per /channels visit; the BFF
    // handles auth refresh transparently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    // Parallel fetch — no inter-call dependencies. We swallow per-call
    // failures so a flaky agents endpoint doesn't blank the whole page.
    const [adaptersRes, bindingsRes, agentsRes] = await Promise.allSettled([
      api<AdapterListResponse>("/channels/adapters"),
      api<ChannelBindingListResponse>("/channels/bindings"),
      api<{ items: { id: string; name: string }[] }>("/agents"),
    ]);
    if (adaptersRes.status === "fulfilled") {
      setAdapters(adaptersRes.value.items);
      setAdaptersError(null);
    } else {
      setAdapters([]);
      setAdaptersError(messageOf(adaptersRes.reason, "Failed to load adapters"));
    }
    if (bindingsRes.status === "fulfilled") {
      setBindings(bindingsRes.value.items);
      setBindingsError(null);
    } else {
      setBindings([]);
      setBindingsError(messageOf(bindingsRes.reason, "Failed to load bindings"));
    }
    if (agentsRes.status === "fulfilled") {
      setAgents(agentsRes.value.items.map((a) => ({ id: a.id, name: a.name })));
    } else {
      setAgents([]);
    }
  }

  function openCreateFor(kind: string | null) {
    setCreateKind(kind);
    setCreateOpen(true);
  }

  async function handleDelete(binding: ChannelBindingSummary) {
    if (!confirm(t("channels.binding.deleteConfirm", { slug: binding.slug }))) {
      return;
    }
    try {
      await api(`/channels/bindings/${binding.id}`, { method: "DELETE" });
      toast.success(t("channels.toast.deleted", { slug: binding.slug }));
      await refresh();
    } catch (err) {
      toast.error(messageOf(err, "Failed to delete binding"));
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("channels.section.adapters")}</CardTitle>
          <CardDescription>{t("channels.section.adapters.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {adaptersError ? (
            <ErrorBanner>{adaptersError}</ErrorBanner>
          ) : adapters === null ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {adapters.map((adapter) => (
                <AdapterCard
                  key={adapter.kind}
                  adapter={adapter}
                  isAdmin={isAdmin}
                  onConfigure={() => openCreateFor(adapter.kind)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="text-base">{t("channels.section.bindings")}</CardTitle>
            <CardDescription>{t("channels.section.bindings.desc")}</CardDescription>
          </div>
          {isAdmin ? (
            <Button size="sm" onClick={() => openCreateFor(null)}>
              <Plus /> {t("channels.action.create")}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {bindingsError ? <ErrorBanner>{bindingsError}</ErrorBanner> : null}
          {bindings === null ? (
            <div className="space-y-2">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          ) : bindings.length === 0 ? (
            <EmptyState>{t("channels.empty.bindings")}</EmptyState>
          ) : (
            <BindingsTable
              bindings={bindings}
              agents={agents}
              isAdmin={isAdmin}
              onEdit={setEditing}
              onDelete={handleDelete}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        {createOpen ? (
          <BindingFormDialog
            mode="create"
            adapters={adapters ?? []}
            agents={agents}
            initialKind={createKind}
            onSubmitted={() => {
              setCreateOpen(false);
              setCreateKind(null);
              void refresh();
            }}
          />
        ) : null}
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        {editing ? (
          <BindingFormDialog
            mode="edit"
            adapters={adapters ?? []}
            agents={agents}
            binding={editing}
            onSubmitted={() => {
              setEditing(null);
              void refresh();
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Adapter card — one per descriptor in the catalog.
// ────────────────────────────────────────────────────────────────────

function AdapterCard({
  adapter,
  isAdmin,
  onConfigure,
}: {
  adapter: AdapterDescriptor;
  isAdmin: boolean;
  onConfigure: () => void;
}) {
  const t = useT();
  const Icon = adapterIcon(adapter.kind);
  const bindable = adapter.status !== "planned";
  // Backend rejects bindings of `planned` kinds; the API binding doesn't
  // need a binding row to be "live" (key management lives elsewhere).
  const ctaKey: TranslationKey =
    adapter.kind === "api"
      ? "channels.action.create"
      : bindable
        ? "channels.action.configure"
        : "common.comingSoon";

  function onClick() {
    if (!isAdmin) {
      toast.info(t("channels.error.adminOnly"));
      return;
    }
    if (!bindable) {
      toast.info(t("common.comingSoon"));
      return;
    }
    onConfigure();
  }

  return (
    <div className="flex items-start gap-3 rounded-md border border-border/70 bg-card/40 p-3">
      <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[13px] font-semibold text-foreground">{adapter.display_name}</h3>
          <StatusBadge status={adapter.status} />
        </div>
        <p className="text-[11px] leading-relaxed text-muted-foreground">{adapter.description}</p>
        <div className="pt-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClick}
            className="h-7 px-2 text-[11px]"
          >
            {t(ctaKey)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AdapterStatus }) {
  const t = useT();
  const map: Record<AdapterStatus, { variant: "lemon" | "outline" | "warn"; key: TranslationKey }> =
    {
      available: { variant: "lemon", key: "channels.status.available" },
      experimental: { variant: "warn", key: "channels.status.experimental" },
      planned: { variant: "outline", key: "channels.status.planned" },
    };
  const cfg = map[status];
  return (
    <Badge variant={cfg.variant} className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]">
      {t(cfg.key)}
    </Badge>
  );
}

// ────────────────────────────────────────────────────────────────────
// Bindings table.
// ────────────────────────────────────────────────────────────────────

function BindingsTable({
  bindings,
  agents,
  isAdmin,
  onEdit,
  onDelete,
}: {
  bindings: ChannelBindingSummary[];
  agents: AgentOption[];
  isAdmin: boolean;
  onEdit: (binding: ChannelBindingSummary) => void;
  onDelete: (binding: ChannelBindingSummary) => void;
}) {
  const t = useT();
  const agentName = useMemo(() => {
    const map = new Map(agents.map((a) => [a.id, a.name]));
    return (id: string): string => map.get(id) ?? id.slice(0, 8);
  }, [agents]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("channels.col.slug")}</TableHead>
          <TableHead>{t("channels.col.kind")}</TableHead>
          <TableHead>{t("channels.col.agent")}</TableHead>
          <TableHead>{t("channels.col.credentials")}</TableHead>
          <TableHead>{t("channels.col.status")}</TableHead>
          {isAdmin ? <TableHead aria-label="actions" /> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {bindings.map((b) => (
          <TableRow key={b.id}>
            <TableCell className="font-mono text-xs">
              {b.slug}
              {b.display_name ? (
                <div className="text-[11px] text-muted-foreground">{b.display_name}</div>
              ) : null}
            </TableCell>
            <TableCell>
              <Badge variant="outline">{b.kind}</Badge>
            </TableCell>
            <TableCell className="text-xs">{agentName(b.agent_id)}</TableCell>
            <TableCell>
              {b.has_credentials ? (
                <Badge variant="ok">{t("channels.creds.in")}</Badge>
              ) : (
                <Badge variant="outline">{t("channels.creds.none")}</Badge>
              )}
            </TableCell>
            <TableCell>
              {b.enabled ? (
                <Badge variant="ok">{t("channels.action.enable")}</Badge>
              ) : (
                <Badge variant="warn">{t("channels.action.disable")}</Badge>
              )}
            </TableCell>
            {isAdmin ? (
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t("channels.action.edit")} ${b.slug}`}
                  onClick={() => onEdit(b)}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t("channels.action.delete")} ${b.slug}`}
                  onClick={() => onDelete(b)}
                >
                  <Trash2 className="text-destructive" />
                </Button>
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ────────────────────────────────────────────────────────────────────
// Add / Edit dialog — single component, mirrors the providers pattern.
// ────────────────────────────────────────────────────────────────────

interface BindingFormProps {
  mode: "create" | "edit";
  adapters: AdapterDescriptor[];
  agents: AgentOption[];
  /** Edit mode requires this; create mode ignores it. */
  binding?: ChannelBindingSummary;
  /** Pre-pick an adapter when opening from a catalog card. */
  initialKind?: string | null;
  onSubmitted: () => void;
}

function BindingFormDialog({
  mode,
  adapters,
  agents,
  binding,
  initialKind,
  onSubmitted,
}: BindingFormProps) {
  const t = useT();

  // Bindable adapters only — the backend rejects `planned` kinds.
  const bindable = useMemo(() => adapters.filter((a) => a.status !== "planned"), [adapters]);

  const [kind, setKind] = useState<string>(binding?.kind ?? initialKind ?? bindable[0]?.kind ?? "");
  const [slug, setSlug] = useState(binding?.slug ?? "");
  const [displayName, setDisplayName] = useState(binding?.display_name ?? "");
  const [agentId, setAgentId] = useState<string>(binding?.agent_id ?? agents[0]?.id ?? "");
  const [configText, setConfigText] = useState<string>(
    JSON.stringify(binding?.config ?? {}, null, 2),
  );
  const [credentialsText, setCredentialsText] = useState<string>("");
  const [dropCredentials, setDropCredentials] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const adapter = useMemo(() => adapters.find((a) => a.kind === kind), [adapters, kind]);
  const title = mode === "create" ? t("channels.dialog.create") : t("channels.dialog.edit");
  const submitLabel = mode === "create" ? t("channels.action.create") : t("channels.action.edit");

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const trimmedSlug = slug.trim();
    if (!trimmedSlug) {
      toast.error(`${t("channels.field.slug")}: required`);
      return;
    }
    if (!agentId) {
      toast.error(`${t("channels.field.agent")}: required`);
      return;
    }

    let parsedConfig: Record<string, unknown>;
    try {
      parsedConfig = configText.trim() === "" ? {} : JSON.parse(configText);
    } catch (err) {
      toast.error(`${t("channels.field.config")}: ${messageOf(err, "invalid JSON")}`);
      return;
    }

    let parsedCredentials: Record<string, unknown> | null | undefined;
    if (mode === "edit" && dropCredentials) {
      parsedCredentials = null;
    } else if (credentialsText.trim()) {
      try {
        parsedCredentials = JSON.parse(credentialsText);
      } catch (err) {
        toast.error(`${t("channels.field.credentials")}: ${messageOf(err, "invalid JSON")}`);
        return;
      }
    } else if (mode === "create") {
      parsedCredentials = null;
    }
    // mode === "edit" && no input + no drop → leave field unset → keep existing.

    setSubmitting(true);
    try {
      if (mode === "create") {
        const body: CreateChannelBindingRequest = {
          kind,
          slug: trimmedSlug,
          display_name: displayName.trim() ? displayName.trim() : null,
          agent_id: agentId,
          config: parsedConfig,
          credentials: parsedCredentials ?? null,
          enabled: true,
        };
        await api("/channels/bindings", { method: "POST", body: JSON.stringify(body) });
        toast.success(t("channels.toast.created", { slug: trimmedSlug }));
      } else {
        if (!binding) {
          toast.error("Edit dialog opened without a binding");
          return;
        }
        const body: UpdateChannelBindingRequest = {};
        if ((displayName.trim() || null) !== (binding.display_name ?? null)) {
          body.display_name = displayName.trim() ? displayName.trim() : null;
        }
        if (JSON.stringify(parsedConfig) !== JSON.stringify(binding.config)) {
          body.config = parsedConfig;
        }
        if (parsedCredentials !== undefined) {
          body.credentials = parsedCredentials;
        }
        if (Object.keys(body).length === 0) {
          toast.message("Nothing changed");
          setSubmitting(false);
          return;
        }
        await api(`/channels/bindings/${binding.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.success(t("channels.toast.updated", { slug: binding.slug }));
      }
      onSubmitted();
    } catch (err) {
      toast.error(messageOf(err, "Failed to save binding"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{adapter?.description}</DialogDescription>
      </DialogHeader>
      <form className="grid gap-4" onSubmit={onSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="binding-kind">{t("channels.col.kind")}</Label>
            <Select value={kind} onValueChange={setKind} disabled={mode === "edit"}>
              <SelectTrigger id="binding-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {bindable.map((a) => (
                  <SelectItem key={a.kind} value={a.kind}>
                    {a.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="binding-slug">{t("channels.field.slug")}</Label>
            <Input
              id="binding-slug"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="feishu-prod"
              maxLength={64}
              disabled={mode === "edit"}
              required
            />
            <p className="text-xs text-muted-foreground">{t("channels.field.slug.hint")}</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="binding-display-name">{t("channels.field.displayName")}</Label>
          <Input
            id="binding-display-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={120}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="binding-agent">{t("channels.field.agent")}</Label>
          <Select value={agentId} onValueChange={setAgentId}>
            <SelectTrigger id="binding-agent">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {adapter?.config_schema ? (
          <div className="space-y-1.5">
            <Label htmlFor="binding-config">{t("channels.field.config")}</Label>
            <Textarea
              id="binding-config"
              value={configText}
              onChange={(event) => setConfigText(event.target.value)}
              rows={6}
              spellCheck={false}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">{t("channels.field.config.hint")}</p>
          </div>
        ) : null}
        {adapter?.needs_credentials ? (
          <div className="space-y-1.5">
            <Label htmlFor="binding-credentials">{t("channels.field.credentials")}</Label>
            <Textarea
              id="binding-credentials"
              value={credentialsText}
              onChange={(event) => setCredentialsText(event.target.value)}
              rows={4}
              spellCheck={false}
              className="font-mono text-xs"
              placeholder='{"app_secret": "…"}'
              disabled={dropCredentials}
            />
            {mode === "edit" && binding?.has_credentials ? (
              <label className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={dropCredentials}
                  onChange={(event) => setDropCredentials(event.target.checked)}
                />
                {t("channels.field.credentials.drop")}
              </label>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {mode === "edit" &&
              binding?.has_credentials &&
              !credentialsText.trim() &&
              !dropCredentials
                ? t("channels.field.credentials.kept")
                : t("channels.field.credentials.hint")}
            </p>
          </div>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("common.cancel")}
            </Button>
          </DialogClose>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="animate-spin" /> : null}
            {submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}
